import type { Contract, ContractSeed, PaymentCert, PaymentProject, PaymentsData } from "./types";

const KEY = "cg:payments";

export function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }
}
export function nowDate(): string {
  try {
    return new Date().toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

export function loadPayments(): PaymentsData {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as PaymentsData;
  } catch {
    /* ignore */
  }
  return { projects: [] };
}
export function savePayments(data: PaymentsData) {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export function exportPayments(data: PaymentsData) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "payment-ledger.json";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
export async function importPayments(file: File): Promise<PaymentsData> {
  const parsed = JSON.parse(await file.text());
  if (!parsed || !Array.isArray(parsed.projects)) throw new Error("Not a valid payment ledger file.");
  return parsed as PaymentsData;
}

export function newProject(name: string): PaymentProject {
  return { id: uid(), name, currency: "USD", contracts: [], createdAt: nowDate() };
}
export function newContract(currency: string): Contract {
  return { id: uid(), name: "", kind: "prime", party: "", currency, certificates: [] };
}
export function newCert(currency: string, number: string): PaymentCert {
  return {
    id: uid(),
    number,
    date: nowDate(),
    kind: "construction",
    description: "",
    amount: NaN,
    currency,
    status: "authorised",
    coversAllOutstanding: true,
    createdAt: nowDate(),
  };
}

/** Build a payment project linked to a contract from the Contract section. */
export function projectFromContract(seed: ContractSeed): PaymentProject {
  const cur = seed.currency || "USD";
  return {
    id: uid(),
    name: seed.projectName || "Contract project",
    reference: seed.reference,
    location: seed.location,
    currency: cur,
    authoriser: seed.employer,
    authoriserTitle: "for the Employer",
    linkedContract: { name: seed.projectName || "Contract", reference: seed.reference },
    atelierProjectId: seed.atelierProjectId,
    createdAt: nowDate(),
    contracts: seed.contractor
      ? [{ id: uid(), name: "Prime Construction Contract", kind: "prime", party: seed.contractor, currency: cur, value: seed.contractValue != null && isFinite(seed.contractValue) ? seed.contractValue : undefined, certificates: [] }]
      : [],
  };
}

/** Seed a worked example (a prime construction contract + a glass supply). */
export function exampleProject(): PaymentProject {
  const cur = "USD";
  return {
    id: uid(),
    name: "Example Tower",
    reference: "EX-2026-01",
    location: "Georgetown, Guyana",
    currency: cur,
    authoriser: "Project Director",
    authoriserTitle: "for the Employer",
    createdAt: nowDate(),
    contracts: [
      {
        id: uid(),
        name: "Prime Construction Contract",
        kind: "prime",
        party: "BuildRight Construction Ltd",
        currency: cur,
        value: 5000000,
        certificates: [
          { id: uid(), number: "PC-001", date: nowDate(), kind: "construction", description: "Substructure and ground-floor slab, works to date", amount: 420000, currency: cur, status: "authorised", coversAllOutstanding: true, createdAt: nowDate() },
        ],
      },
      {
        id: uid(),
        name: "Glazing / Curtain Wall Supply",
        kind: "supply",
        party: "ClearView Glass Co.",
        supplier: "ClearView Glass Co.",
        currency: cur,
        certificates: [
          { id: uid(), number: "SP-GL-001", date: nowDate(), kind: "material-supply", description: "Supply of tempered glass units for levels 1–3", invoiceRef: "CV-INV-2291", invoiceDate: nowDate(), amount: 86500, currency: cur, status: "authorised", coversAllOutstanding: true, createdAt: nowDate() },
        ],
      },
    ],
  };
}
